/**
 * TelehealthAdmin — the practitioner's telehealth page.
 *
 * Backend: `GET /api/tenant/consults` (the list) and `GET/PUT /api/tenant/telehealth/
 * provider` (which video provider mints the room).
 *
 * A CONSULTATION IS A SERVICE DELIVERED BY VIDEO. It is created on
 * `/admin/booking-services` like any other: a name, a price, a length, and
 * `delivery_mode: "video"`. It is paid in full at booking through the ordinary checkout,
 * its room opens on its own window, and nothing is metered while the call runs.
 *
 * THE PER-MINUTE LANE IS WITHDRAWN FROM THIS SCREEN, NOT FROM THE PRODUCT. A practice that
 * already sells a per-minute consultation keeps selling it, and this page still lists those
 * consultations beside the fixed-block ones (the list is keyed on `delivery_mode`, which
 * both models carry). What is gone from here is the rate card that created them.
 *
 * WHY THE LIST IS SESSIONS AND NOT ROOMS. This page used to render `GET /telehealth/rooms`
 * beside an hourly-rate "services" manager and a hand-typed complete-and-refund dialog —
 * the legacy prepaid lane, a second telehealth money model living on the same screen as
 * Flow C. That lane is gone. A room is a door; the SESSION is the sale: it carries the
 * server-measured minutes, the captured amount and the evidence grade, which are the three
 * things a practitioner actually comes here to read.
 *
 * SELLING A CONSULTATION NEEDS ONE THING NOW: a service with `delivery_mode: "video"`
 * (`/admin/booking-services`). The hint under the header is what makes its absence visible
 * here rather than leaving this page mysteriously empty.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Video, AlertTriangle, ExternalLink, CheckCircle2, ListOrdered, Loader2, RefreshCw } from "lucide-react";
import ConsultCompleteDialog from "./telehealth/ConsultCompleteDialog";
import ConsultTimelineDialog from "./telehealth/ConsultTimelineDialog";
import { bucketConsult, CONSULT_BUCKETS, isNotOpened } from "./telehealth/consultBuckets";
// Relative: gateway/ has no "@/" Jest shim (see gateway/__testutils__/mocks.js), and
// `money`/`calendarUtils` are asserted to the cent and to the locale by the tests.
import defaultGatewayApi from "../gateway/api";
import { CONSULT_AUTH_STATES, isFixedBlock } from "../gateway/contract";
import { useTenantConsults } from "../gateway/hooks/useTenantConsults";
import { fmtMoney } from "./money";
import { localeFor } from "./booking/calendarUtils";
import { adminHref } from "./adminPath";
import { useEntitlements } from "./entitlements/EntitlementsContext";
import { CAP } from "./entitlements/capabilities";
import CapabilityNotice from "./entitlements/CapabilityNotice";

// Where a row may still be acted on. `bucketConsult` files every terminal state (and any
// state this build does not know) under "done", so "not done" IS "not terminal" — one
// definition, not a second list of states drifting beside the first.
const isTerminal = (bucket) => bucket === "done";
// Complete / no-show / cancel decide money, and only these two states have a decision
// left to make: past `computed` the minutes are measured and the capture is settling.
//
// A NEVER-OPENED ROW BELONGS HERE TOO (F-06). It has no session — nothing to complete and
// no minutes to measure — but it may be holding a live authorisation on a patient's card,
// and the dialog is the ONLY surface offering to release one. `ConsultCompleteDialog`
// reads the same fact from its own session lookup and offers Cancel alone there, so the
// two actions that need a session are never put in front of a consult that has none.
// A FIXED BLOCK HAS NO AUTHORISATION TO BE IN A STATE, and the server says so by sending
// `authState: null` rather than "none" — "this question does not apply" is not "we asked
// and the answer was no". Reading it through `authStateOf` would have put "not authorised"
// on the practitioner's list beside a consultation their client has already paid for.
//
// What such a row states instead is the APPOINTMENT's own status, through the label family
// the bookings grid already owns: one vocabulary for one fact, rather than a second set of
// words for the same six statuses.
//
// WHICH ROWS THOSE ARE IS `pricingMode`, the server's own statement, via the contract's
// one predicate — never `authState == null`, which is an ABSENCE with several causes: a
// metered row whose payment lookup failed has a null `authState` too, and inferring the
// model from it silently pilled that consultation as a paid fixed block and withheld the
// money actions its live hold still needed.

//
// NEVER FOR A FIXED BLOCK. That dialog's three actions are all money actions —
// `/complete` and `/no-show` capture out of a hold, and the consult surface's cancel
// releases one — and a fixed block has no hold: all three refuse. It is permanently a
// "never opened" row (it has no session by design), so without this term the practitioner
// would be offered a dialog on which nothing works. An ordinary appointment is cancelled
// from the bookings grid, which is what a fixed-block consultation is.
const canDecideMoney = (row) => !isFixedBlock(row)
  && (["scheduled", "in_progress"].includes(row.sessionStatus) || isNotOpened(row));

const STATUS_STYLE = {
  scheduled: "bg-amber-100 text-amber-700",
  in_progress: "bg-emerald-100 text-emerald-700",
  computing: "bg-sky-100 text-sky-700",
  computed: "bg-sky-100 text-sky-700",
  captured: "bg-eh-cream text-eh-muted",
  void_no_show: "bg-rose-100 text-rose-700",
  cancelled: "bg-rose-100 text-rose-700",
};

// The pill of a row with NO SESSION: its state is the state of its MONEY, because that is
// the only thing that has happened to it yet. `authorised` is the F-06 row — a live hold
// on a room nobody ever entered — and it is deliberately not styled as a calm "scheduled":
// it is the one the practitioner has to act on.
const AUTH_STYLE = {
  none: "bg-eh-cream text-eh-muted",
  awaiting: "bg-amber-100 text-amber-700",
  authorised: "bg-sky-100 text-sky-700",
  failed: "bg-rose-100 text-rose-700",
};
// An `authState` this build does not know renders as "none" — "no authorisation we can
// vouch for" — rather than as a raw identifier. The family is pinned against
// `schemas.CONSULT_AUTH_STATES` by i18n/__tests__/keyCoverage.test.js, so a real fifth
// state is a red test rather than a leaked backend string on a practitioner's screen.
const authStateOf = (row) =>
  (CONSULT_AUTH_STATES.includes(row?.authState) ? row.authState : "none");

export default function TelehealthAdmin({ gatewayApi = defaultGatewayApi }) {
  const t = useT("admin");
  const { lang } = useLanguage();

  // READ, NEVER WRITTEN, from this screen. With one supported provider a radio group is
  // a control that cannot be operated, and removing it takes `PUT /tenant/telehealth/
  // provider` off the practitioner surface entirely rather than leaving a disabled
  // affordance behind. `null` = not read yet, which renders nothing at all.
  const [defaultProvider, setDefaultProvider] = useState(null);
  // `null` = not read yet, and it is NOT the same as `[]`: an empty catalogue is a fact
  // ("no per-minute service"), an unread one is not, and claiming the first while the
  // second is true puts a false alarm on a correctly configured practice.
  const [services, setServices] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [completeId, setCompleteId] = useState(null);
  const [timelineId, setTimelineId] = useState(null);
  // KEYED BY receiptNumber, not appointmentId: two rows can each be mid-resend at once,
  // and each button's own click must disable and re-enable only ITSELF. A single scalar
  // "the id currently resending" would let a second row's click stay disabled forever
  // once a first one settled, or (worse) let the two closures share one flag.
  const [resendingReceipts, setResendingReceipts] = useState(() => new Set());

  const { rows, loading, error, reload } = useTenantConsults({}, gatewayApi);
  // ONE effect, one liveness flag, declared INSIDE the effect body. A `useRef(true)`
  // cleared in a cleanup is a LATCH under StrictMode (React mounts, cleans up, re-mounts
  // and nothing sets the ref back), which is how a page ends up loading for ever —
  // see admin/__tests__/TelehealthAdmin.strictMode.test.js.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/tenant/telehealth/provider");
        if (!cancelled) setDefaultProvider(data?.provider ?? null);
      } catch (e) {
        // Not an error the practitioner can act on: the card simply shows no selection.
        console.warn("[telehealth] provider load failed:", e?.message);
      }
      try {
        const { data } = await api.get("/booking/services");
        if (!cancelled) setServices(Array.isArray(data) ? data : []);
      } catch (e) {
        // Left `null`: an unreadable catalogue must not raise "you have no per-minute
        // service", which is a claim about the catalogue we failed to read.
        console.warn("[telehealth] services load failed:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const closeComplete = useCallback((didMutate) => {
    setCompleteId(null);
    if (didMutate) reload();
  }, [reload]);

  const closeTimeline = useCallback(() => { setTimelineId(null); }, []);

  // The row is the only source of its own receipt number: capturing `row` in the closure
  // (rather than reading some shared "the active row" state) is what keeps two rows'
  // clicks from ever sending each other's number.
  const resendReceipt = useCallback(async (row) => {
    const receiptNumber = row.receiptNumber;
    setResendingReceipts((prev) => new Set(prev).add(receiptNumber));
    try {
      await gatewayApi.resendReceipt(receiptNumber);
      toast.success(t("consult.list.receiptResent", { email: row.patientEmail }));
    } catch (e) {
      toast.error(extractApiError(e, t("consult.list.receiptResendError")));
    } finally {
      setResendingReceipts((prev) => {
        const next = new Set(prev);
        next.delete(receiptNumber);
        return next;
      });
    }
  }, [gatewayApi, t]);

  // Bucketed once per (rows, render): `now` is read here rather than per row, so two rows
  // in one paint cannot be classified against two different instants.
  const buckets = useMemo(() => {
    const nowIso = new Date().toISOString();
    const out = { upcoming: [], inProgress: [], awaitingCompletion: [], done: [] };
    for (const row of rows || []) out[bucketConsult(row, nowIso)].push(row);
    return out;
  }, [rows]);

  // A VIDEO SERVICE, not a per-minute one. The advisory exists to say "this screen has
  // nothing to show you because you sell no consultations", and a practice selling
  // fixed-block ones sells consultations — reading `pricing_mode` here told them they had
  // none while their list filled up underneath it.
  const hasVideoService = (services || []).some(
    (s) => s?.active && s?.delivery_mode === "video");
  // `care.video_rooms` withholds NEW video business only. Everything below the header —
  // the consult list, the room, complete / no-show / cancel, the timeline, receipts —
  // serves consultations already sold and stays whatever the plan says. When the plan
  // lacks video (or it is not built), the one invitation on this page to start selling
  // video — the "create a video service" nudge, which leads to a service the server now
  // refuses — gives way to the plan notice. `null` (map not known yet) claims nothing.
  const ent = useEntitlements();
  const videoState = ent.status(CAP.videoRooms);
  const videoWithheld = videoState === "not_in_plan" || videoState === "coming_soon";
  const showNoService = !videoWithheld && services !== null && !hasVideoService;

  const visible = buckets[tab] || [];
  const listEmpty = (rows || []).length === 0;

  const fmtWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(localeFor(lang), { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div data-testid="telehealth-admin" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <Video className="w-6 h-6 text-sol-gold" /> {t("tele.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">{t("tele.subtitle")}</p>
      </div>

      <CapabilityNotice
        state={videoState}
        eta={ent.eta(CAP.videoRooms)}
        body={t("tele.planNotice")}
        testId="tele-plan-notice"
      />

      {showNoService && (
        <div
          role="status"
          className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
          data-testid="tele-no-video-service"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {t("tele.noVideoService")}{" "}
            <Link to={adminHref("/admin/booking-services")} className="underline font-medium" data-testid="tele-no-video-service-link">
              {t("tele.noVideoServiceLink")}
            </Link>
          </span>
        </div>
      )}

      {/* THE PER-MINUTE RATE CARD IS WITHDRAWN FROM THE UI, not from the product. A
          practice that already sells a per-minute consultation keeps selling it — the rate
          row, the ceiling, the clock and the capture are all untouched, and the super-admin
          console still owns those rows — but a practitioner can no longer create or edit
          one, because consultations are now sold as fixed blocks like every other service.
          `ConsultRateSettings` is deliberately left on disk rather than deleted: the lane
          it configures is still live, and deleting the only screen that explains it would
          strand it. */}
      {/* THE VIDEO PROVIDER IS A FACT, NOT A CHOICE. Daily is the only supported
          provider (`SUPPORTED_TELEHEALTH`), so the picker that stood here offered four
          buttons of which three refused the room they claimed to open, and the fourth
          was already what every consultation used.

          IT RENDERS THE SERVER'S WORD, and never launders it into "Daily.co"
          client-side: a tenant document still carrying a retired provider is a fact
          about the estate that the operator needs to see here, and a screen that
          rewrote it would report a migration as done while it was not. Nothing is
          rendered at all until the GET answers — an absent value is not a provider. */}
      {defaultProvider && (
        <div className="bg-white rounded-2xl border border-eh-border-soft p-6" data-testid="telehealth-default-provider">
          <h3 className="font-display text-xl mb-1">{t("tele.videoProvider")}</h3>
          <p className="text-lg text-eh-ink" data-testid="telehealth-provider-value">
            {/* A BRAND NAME IS A PROPER NOUN, NOT PROSE: "Daily.co" has no three
                translations, so it is a bare literal and not a dictionary key. */}
            {defaultProvider === "daily" ? "Daily.co" : defaultProvider}
          </p>
          <p className="text-eh-muted text-sm mt-2">{t("tele.videoProviderNote")}</p>
        </div>
      )}

      <div className="space-y-3" data-testid="consult-list">
        <h3 className="font-display text-xl">{t("consult.list.title")}</h3>

        <div className="flex flex-wrap gap-2">
          {CONSULT_BUCKETS.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setTab(b)}
              data-testid={`consult-tab-${b}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${tab === b ? "bg-eh-ink text-white border-eh-ink" : "bg-white text-eh-muted border-eh-border-soft hover:border-eh-ink"}`}
            >
              {t(`consult.list.tab.${b}`)} ({buckets[b].length})
            </button>
          ))}
        </div>

        {loading && rows === null && (
          <p className="text-center text-eh-muted py-10 flex items-center justify-center gap-2" data-testid="consult-list-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("consult.list.loading")}
          </p>
        )}

        {error && rows === null && (
          <div className="text-center py-10 space-y-2" data-testid="consult-list-error">
            <p className="text-sm text-rose-600" role="alert">{t("consult.list.loadError")}</p>
            <button type="button" onClick={reload} data-testid="consult-list-retry" className="text-sm underline text-eh-sage-deep">
              {t("consult.list.retry")}
            </button>
          </div>
        )}

        {rows !== null && listEmpty && (
          <p className="text-center text-eh-muted py-12" data-testid="consult-list-empty-all">{t("tele.empty")}</p>
        )}

        {rows !== null && !listEmpty && visible.length === 0 && (
          <p className="text-center text-eh-muted py-10" data-testid="consult-list-empty">{t(`consult.list.empty.${tab}`)}</p>
        )}

        {visible.map((r) => {
          const bucket = bucketConsult(r);
          // NEVER RENDERED FROM A GUESSED CURRENCY. `r.currency` is `/tenant/consults`'s
          // own field; a captured figure with no currency (or one still null because the
          // capture has not landed) is exactly as unstateable as a genuinely missing
          // amount, and must fall to the same minutes-only rendering rather than let
          // `fmtMoney` guess a code the server never actually stated (same fix as
          // `admin/telehealth/ConsultRoomPage.jsx`'s F1).
          const billed = r.billableMinutes != null && r.capturedCents != null && !!r.currency;
          const measuredOnly = r.billableMinutes != null && !billed;
          return (
            <div
              key={r.appointmentId}
              data-testid={`consult-row-${r.appointmentId}`}
              className="bg-white rounded-2xl border border-eh-border-soft p-4 flex flex-wrap items-center gap-4"
            >
              <div className="flex-1 min-w-[14rem]">
                <div className="flex gap-2 items-center mb-1">
                  <span
                    data-testid={`consult-status-${r.appointmentId}`}
                    className={`text-[10px] uppercase px-2 py-0.5 rounded ${(isFixedBlock(r) ? "" : isNotOpened(r) ? AUTH_STYLE[authStateOf(r)] : STATUS_STYLE[r.sessionStatus]) || "bg-eh-cream text-eh-muted"}`}
                  >
                    {/* No raw-identifier fallback: a `labels[x] || x` escape hatch is
                        exactly how `pending_payment` reached a customer in three
                        languages. Both families are pinned against their Python
                        declaration by i18n/__tests__/keyCoverage.test.js, so a missing
                        label is a red test, not a leaked backend string.

                        A row with no session states its MONEY instead: nothing else has
                        happened to it yet, and "—" (what this rendered before) said
                        nothing at all about a consultation that may be holding a live
                        card. */}
                    {isFixedBlock(r)
                      ? t(`bookings.statusLabel.${r.bookingStatus}`, null, r.bookingStatus)
                      : isNotOpened(r)
                        ? t(`consult.list.auth.${authStateOf(r)}`)
                        : t(`consult.list.status.${r.sessionStatus}`)}
                  </span>
                  <span className="text-xs text-eh-muted">{fmtWhen(r.scheduledStartAt)}</span>
                </div>
                <div className="font-medium text-eh-ink truncate">{r.patientName || r.patientEmail}</div>
                <div className="text-xs text-eh-muted truncate">
                  {r.patientEmail}{r.serviceName ? ` · ${r.serviceName}` : ""}
                </div>
                {/* Stated only once the SERVER has both measured and captured. A zero
                    rendered from an absent figure reads as "nothing was billed", which is
                    the one conclusion that would be wrong for a consult still running. */}
                {billed && (
                  <div className="text-xs text-eh-ink mt-1 tabular-nums" data-testid={`consult-billed-${r.appointmentId}`}>
                    {t("consult.list.billed", {
                      minutes: r.billableMinutes,
                      amount: fmtMoney(r.capturedCents, r.currency),
                    })}
                  </div>
                )}
                {measuredOnly && (
                  <div className="text-xs text-eh-muted mt-1 tabular-nums" data-testid={`consult-minutes-${r.appointmentId}`}>
                    {t("consult.list.billedPending", { minutes: r.billableMinutes })}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                {!isTerminal(bucket) && (
                  <Link
                    to={adminHref(`/admin/telehealth/consult/${r.appointmentId}`)}
                    data-testid={`consult-open-room-${r.appointmentId}`}
                    title={t("consult.list.openRoom")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-eh-border-soft hover:border-sol-gold"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> {t("consult.list.openRoom")}
                  </Link>
                )}
                {canDecideMoney(r) && (
                  <button
                    type="button"
                    onClick={() => setCompleteId(r.appointmentId)}
                    data-testid={`consult-complete-${r.appointmentId}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-eh-border-soft hover:border-sol-gold"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> {t("consult.list.complete")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setTimelineId(r.appointmentId)}
                  data-testid={`consult-timeline-${r.appointmentId}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-eh-border-soft hover:border-sol-gold"
                >
                  <ListOrdered className="w-3.5 h-3.5" /> {t("consult.list.timeline")}
                </button>
                {/* Never shown when no receipt has been issued yet — `receiptNumber` is
                    null until `post_capture` writes one, and there is nothing to resend. */}
                {r.receiptNumber && (
                  <button
                    type="button"
                    onClick={() => resendReceipt(r)}
                    disabled={resendingReceipts.has(r.receiptNumber)}
                    data-testid={`consult-resend-receipt-${r.appointmentId}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-eh-border-soft hover:border-sol-gold disabled:opacity-50"
                  >
                    {resendingReceipts.has(r.receiptNumber)
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5" />}
                    {t("consult.list.resendReceipt")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ConsultCompleteDialog
        appointmentId={completeId}
        open={!!completeId}
        onClose={closeComplete}
        gatewayApi={gatewayApi}
      />
      <ConsultTimelineDialog
        appointmentId={timelineId}
        open={!!timelineId}
        onClose={closeTimeline}
        gatewayApi={gatewayApi}
      />
    </div>
  );
}
